import { FC, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { Box, Button, Chip, CircularProgress, IconButton, Stack, Typography, Skeleton, Divider } from '@mui/material';
import HighlightOffTwoToneIcon from '@mui/icons-material/HighlightOffTwoTone';
import LoadingButton from "@mui/lab/LoadingButton";
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import InvoicePdfComparison from '../../components/Invoice/InvoicePdfComparison';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { toast } from 'react-toastify';
import { PdfMissingState } from './InvoicePreview';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import jwt_decode from 'jwt-decode';
import { fetchInvoiceById, fetchInvoicePreview, fetchInvoices, regenerateInvoicePdf } from '../../store/invoiceStore/invoiceThunk';
import { AInvoice, InvoicePreviewPayload, PdfGenerationError } from "../../utils/invoice.types";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import axios from '../../utils/axios';
import { Utils, VocuherCategory } from "../../utils/common-utils";
import InvoiceNavigationBar, { NavigationFilters } from '../../components/Invoice/InvoiceNavigationBar';
import InvoicePaymentBreakdown, { DETAIL_GRID_SX, FULL_ROW_CLASS, useInvoicePayments } from '../../components/Invoice/InvoicePaymentBreakdown';
// Enhanced Preview Invoice page with navigation filters
// `embedded`: rendered inside the invoice form drawer — no URL changes, no writes to the shared invoice store.
const InvoicePreviewWithFilters: FC<{ embedded?: boolean; invoiceId?: string; templateId?: string; type?: string; onClose?: () => void }> = (props) => {
  const { embedded } = props;
  const params = useParams();
  const invoiceId = embedded ? props.invoiceId : params.invoiceId;
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [embeddedTemplateId, setEmbeddedTemplateId] = useState<string | null>(props.templateId || null);
  const templateId = embedded ? embeddedTemplateId : searchParams.get("templateId");
  const voucherType = embedded ? props.type || '' : searchParams.get("type") || '';
  // Filters the listing had applied when this invoice was opened. Without them the
  // preview navigated every invoice in the account (329) instead of the filtered
  // list the user was looking at (106).
  const listFilters = searchParams.get("listFilters") || '';

  const dispatch = useAppDispatch();
  const { isLoggedIn, selectedAccount } = useAppSelector((state) => state.auth);
  const invoiceDetails = useAppSelector(state => state.invoices.invoiceDetails);
  // fetchInvoices clears the store's invoiceDetails, so keep our own copy.
  const [fetchedDetails, setFetchedDetails] = useState<any>(null);
  const loadInvoiceDetails = (id: string) =>
    (embedded ? axios.get(`/api/v1/invoice/${id}`).then((r: any) => ({ payload: r.data })) : dispatch(fetchInvoiceById(id))).then((res: any) => {
      if (res?.payload?._id) setFetchedDetails(res.payload);
    });

  const [pdfLocation, setPdfLocation] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isRefersh, setIsRefersh] = useState("");
  const [currentInvoiceId, setCurrentInvoiceId] = useState(invoiceId);
  // PDF (re)generation state: stored PDF missing / broken, regeneration running, and the
  // structured failure reason (missing fields, template error) returned by the backend.
  const [pdfMissing, setPdfMissing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [pdfError, setPdfError] = useState<PdfGenerationError | null>(null);
  const [viewerKey, setViewerKey] = useState(0);
  // Previous PDF kept after a regeneration, so the two versions can be wiped between.
  const [previousPdfLocation, setPreviousPdfLocation] = useState<string>("");
  const [showComparison, setShowComparison] = useState(false);
  const canRegenerate = isLoggedIn && !!templateId && !!currentInvoiceId;

  const handleRegeneratePdf = async () => {
    if (!canRegenerate) return;
    setIsRegenerating(true);
    setPdfError(null);
    const result: any = await dispatch(regenerateInvoicePdf({ invoiceId: currentInvoiceId, templateId }));
    setIsRegenerating(false);
    if (regenerateInvoicePdf.fulfilled.match(result) && result.payload?.Location) {
      setPdfLocation(result.payload.Location);
      setPdfMissing(false);
      setViewerKey(k => k + 1);
      const prev = result.payload.previousLocation;
      setPreviousPdfLocation(prev && prev !== result.payload.Location ? prev : "");
      setShowComparison(false);
      // Backend may have resolved a real template for a migrated ('DEFAULT') invoice; keep the URL in sync.
      const resolvedTemplateId = result.payload.templateId;
      if (resolvedTemplateId && resolvedTemplateId !== templateId && embedded) {
        setEmbeddedTemplateId(resolvedTemplateId);
      } else if (resolvedTemplateId && resolvedTemplateId !== templateId) {
        const next = new URLSearchParams(searchParams);
        next.set('templateId', resolvedTemplateId);
        setSearchParams(next, { replace: true });
      }
      toast.success('PDF generated successfully');
      return;
    }
    const err: PdfGenerationError = result.payload || { message: 'PDF generation failed.' };
    setPdfError(err);
    setPdfMissing(true);
    toast.error(err.message || 'PDF generation failed.');
  };

  // Navigation window. The list endpoint is paged, and the store's `invoices` is replaced on every
  // fetch, so the pages the user has walked through are accumulated here instead. `indexOffset` is
  // the absolute position of windowInvoices[0] in the full (filtered) list, which is what lets the
  // counter read "45 of 106" and lets navigation continue from the invoice that was opened.
  const [windowInvoices, setWindowInvoices] = useState<AInvoice[]>([]);
  // id -> tag; invoice.tags may be bare ids or populated objects depending on the endpoint.
  const [tagMap, setTagMap] = useState<Record<string, { name: string; color?: string }>>({});
  const [indexOffset, setIndexOffset] = useState(0);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  // First load of the navigation list; the toolbar renders right away and only prev/next wait for it
  const [listLoading, setListLoading] = useState(true);
  const [filters, setFilters] = useState<NavigationFilters>({
    status: 'ALL', dateFrom: null, dateTo: null, partyName: '', amountMin: '', amountMax: '',
  });
  const pageSize = 20;

  // Use the fetched invoice for this id; fall back to the listing row until it arrives.
  const listRow = windowInvoices.find(row => row._id === currentInvoiceId);
  const details: any = [invoiceDetails, fetchedDetails].find((d: any) => d && d._id === currentInvoiceId)
    || listRow || null;

  // Never print "NaN": a record we cannot price yet reads as a dash.
  const previewAmount = (() => {
    if (!details) return '—';
    const charges = Number(Utils.additionalChargesSum(details)) || 0;
    // Drafts store finalAmount as 0, so 0 falls back to amount + charges (same rule as the listing).
    const raw = Number(details.finalAmount) ? details.finalAmount : (Number(details.amount) || 0) + charges;
    const value = Number(raw);
    return Number.isFinite(value) ? Utils.formatNumber(value.toFixed(2)) : '—';
  })();

  // Payments on this invoice for the breakdown (logged-in only: the API needs auth).
  const isPurchaseDoc = (details?.voucherCategory || voucherType) === VocuherCategory.PURCHASE;
  useEffect(() => {
    if (!isLoggedIn) return;
    axios.get('/api/v1/tag?appliesTo=INVOICE')
      .then((res: any) => {
        const map: Record<string, { name: string; color?: string }> = {};
        (res.data || []).forEach((t: any) => { if (t?._id) map[t._id] = { name: t.name, color: t.color }; });
        setTagMap(map);
      })
      .catch(() => setTagMap({}));
  }, [isLoggedIn]);

  const invoiceTags = (Array.isArray(details?.tags) ? details.tags : [])
    .map((tag: any) => (tag?.name ? tag : tagMap[tag?._id || tag]))
    .filter(Boolean);

  const { payments, loading: paymentsLoading } = useInvoicePayments(isLoggedIn ? currentInvoiceId : undefined, isPurchaseDoc, isRefersh);

  const loadedEnd = indexOffset + windowInvoices.length;
  const hasMoreData = totalCount === undefined ? true : loadedEnd < totalCount;
  const hasPrevData = indexOffset > 0;

  /**
   * Query for one page of the list endpoint: the listing's filters as the base, with
   * this screen's own filter bar layered on top (so it can only narrow further, and
   * never sends a duplicate key the backend would read as an array).
   */
  const buildPageParams = (f: NavigationFilters, page: number, locateInvoiceId?: string) => {
    const params = new URLSearchParams(listFilters);
    // Paging and cache-busting belong to whoever is asking, not to the inherited filters.
    ['page', 'limit', 'locateInvoiceId', '_t'].forEach(key => params.delete(key));

    if (f.status && f.status !== 'ALL') params.set('status', f.status);
    // `start`/`end` are only honoured together, so a one-sided range is widened to the other end.
    if (f.dateFrom || f.dateTo) {
      const from = f.dateFrom || new Date('1970-01-01');
      const to = f.dateTo || new Date();
      params.set('start', from.toISOString());
      params.set('end', to.toISOString());
    }
    if (f.partyName.trim()) params.set('partyName', f.partyName.trim());
    // Likewise the amount range needs both bounds present.
    if (f.amountMin || f.amountMax) {
      params.set('amountStartRange', String(f.amountMin || 0));
      params.set('amountEndRange', String(f.amountMax || Number.MAX_SAFE_INTEGER));
    }

    params.set('page', String(page));
    params.set('limit', String(pageSize));
    if (locateInvoiceId) params.set('locateInvoiceId', locateInvoiceId);

    return `&${params.toString()}`;
  };



  // Update currentInvoiceId when URL params change
  useEffect(() => {
    if (invoiceId && invoiceId !== currentInvoiceId) {
      setCurrentInvoiceId(invoiceId);
      setIsLoading(true);
    }
  }, [invoiceId]);

  // Embedded: same endpoint as fetchInvoices, without its reducer clearing the form's invoiceDetails.
  const listInvoices = (args: { accountId: string; type: VocuherCategory; params: string }) => embedded
    ? axios.get(`/api/v1/invoice/account/${args.accountId}/${args.type === VocuherCategory.SALE ? 'sales' : 'purchases'}?${args.params}`)
      .then((r: any) => ({ payload: r.data }), () => ({ payload: null }))
    : dispatch(fetchInvoices(args));

  /** One page of the list endpoint, with the active filters applied server-side. */
  const fetchPage = async (page: number, locateInvoiceId?: string) => {
    const { _id: accountId } = selectedAccount;
    if (!accountId || !isLoggedIn || !voucherType) return null;
    const type = voucherType === VocuherCategory.SALE ? VocuherCategory.SALE : VocuherCategory.PURCHASE;
    // The endpoint pages by `page` (0-based) x `limit` — it ignores `skip`.
    const result: any = await listInvoices({
      accountId,
      type,
      params: buildPageParams(filters, page, locateInvoiceId),
    });
    return result.payload || null;
  };

  /**
   * Builds the navigation window around the invoice that is open: asks the server where that
   * invoice sits in the filtered list, then loads the page holding it. Without this the preview
   * always restarted at the newest 20 invoices, even when the user opened invoice 45 of 106.
   */
  const loadWindowAroundCurrent = async () => {
    setListLoading(true);
    try {
      await loadWindow();
    } finally {
      setListLoading(false);
    }
  };

  const loadWindow = async () => {
    const probe = await fetchPage(0, currentInvoiceId);
    if (!probe) return;
    const total = typeof probe.total === 'number' ? probe.total : undefined;
    setTotalCount(total);

    const position = typeof probe.currentIndex === 'number' ? probe.currentIndex : null;
    const targetPage = position === null ? 0 : Math.floor(position / pageSize);

    let rows: AInvoice[] = probe.invoices || [];
    let offset = 0;
    if (targetPage > 0) {
      const page = await fetchPage(targetPage);
      if (page?.invoices?.length) {
        rows = page.invoices;
        offset = targetPage * pageSize;
      }
    }
    setWindowInvoices(rows);
    setIndexOffset(offset);

    // Filters can exclude the invoice that is open — land on the first match instead of leaving
    // the navigation pointing at an invoice that is no longer in the list.
    if (rows.length && !rows.some(r => r._id === currentInvoiceId)) {
      handleInvoiceChange(rows[0]._id, rows[0].pdfTemplate, rows[0].voucherCategory || '');
    }
  };

  // Initial load, and a reload whenever the account, voucher type or filters change.
  useEffect(() => {
    loadWindowAroundCurrent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount._id, isLoggedIn, voucherType, filters, listFilters]);

  // Load current invoice details when ID changes
  useEffect(() => {
    if (currentInvoiceId && isLoggedIn) {
      loadInvoiceDetails(currentInvoiceId);
    }
  }, [currentInvoiceId, isRefersh, dispatch, isLoggedIn]);

  useEffect(() => {
    // Stops a pending PDF wait once the user moves to another invoice.
    let cancelled = false;
    const getPreview = async () => {
      console.log('Fetching preview for invoice:', currentInvoiceId, 'with template:', templateId);
      let reqObj: InvoicePreviewPayload = {
        invoiceId: currentInvoiceId,
        templateId: templateId
      };
      let invoicePreviewResponse: any;
      let entry: any = null;
      // A just-saved invoice's PDF is rebuilt in the background: keep loading (up to ~60s) rather than show the old one.
      for (let tries = 0; ; tries++) {
        invoicePreviewResponse = await dispatch(fetchInvoicePreview(reqObj));
        if (cancelled) return;
        entry = Array.isArray(invoicePreviewResponse?.payload) ? invoicePreviewResponse.payload[0] : invoicePreviewResponse?.payload;
        if (!entry?.pdfStale || tries >= 20) break;
        setIsLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 3000));
        if (cancelled) return;
      }
      const location = entry?.pdfStale ? null : (entry?.Location || null);
      setPdfLocation(location || '');
      // No stored PDF for this template, the S3 file behind it is gone, or the rebuild never finished -> show "Generate PDF".
      setPdfMissing(!location || !!entry?.pdfMissing);
      setPdfError(null);
      setPreviousPdfLocation("");
      setShowComparison(false);
      setIsLoading(false);
    };

    if (templateId && currentInvoiceId) {
      getPreview();
    } else if (currentInvoiceId) {
      try {
        let token: any = jwt_decode(currentInvoiceId);
        setPdfLocation(token.pdfLcation);
        setIsLoading(false);
      } catch (error) {
        console.log("Failed! Invalid Token", error);
        setIsLoading(false);
      }
    }
    return () => { cancelled = true; };
  }, [currentInvoiceId, isRefersh, templateId, dispatch]);

  const actionChangeHandler = (data: any) => {
    setIsRefersh(data.isRefersh);
  }

  const handleInvoiceChange = (newInvoiceId: string, newTemplateId: string, vCategory: string) => {
    if (newInvoiceId !== currentInvoiceId) {
      setCurrentInvoiceId(newInvoiceId);
      setIsLoading(true);
      setPdfLocation(''); // Clear old PDF
      setPdfMissing(false);
      setPdfError(null);

      // Fetch invoice details when changing invoice
      if (isLoggedIn) {
        loadInvoiceDetails(newInvoiceId);
      }

      // Update URL with new invoice ID
      console.log("vCategory from handleInvoiceChange 111", vCategory)
      if (embedded) return setEmbeddedTemplateId(newTemplateId || templateId);
      const newVoucherType = vCategory === VocuherCategory.SALE ? VocuherCategory.SALE : VocuherCategory.PURCHASE;
      // Keep the listing's filters on the URL, otherwise stepping to the next invoice
      // would drop back to the unfiltered list.
      const keepFilters = listFilters ? `&listFilters=${encodeURIComponent(listFilters)}` : '';
      navigate(`/invoice/${newInvoiceId}/preview?templateId=${newTemplateId || templateId}&type=${newVoucherType}${keepFilters}`, {
        replace: true
      });
    }
  };

  /** Append the next page so navigation can keep moving forward through all matching invoices. */
  const handleLoadMore = async () => {
    if (!hasMoreData || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await fetchPage(Math.floor(loadedEnd / pageSize));
      const rows: AInvoice[] = next?.invoices || [];
      if (rows.length) {
        const known = new Set(windowInvoices.map(i => i._id));
        setWindowInvoices(prev => [...prev, ...rows.filter(r => !known.has(r._id))]);
      }
      if (typeof next?.total === 'number') setTotalCount(next.total);
    } finally {
      setLoadingMore(false);
    }
  };

  /** Prepend the previous page when the user navigates back past the start of the window. */
  const handleLoadPrevious = async () => {
    if (!hasPrevData || loadingMore) return;
    setLoadingMore(true);
    try {
      const prevPage = Math.max(0, Math.floor(indexOffset / pageSize) - 1);
      const prev = await fetchPage(prevPage);
      const rows: AInvoice[] = prev?.invoices || [];
      if (rows.length) {
        const known = new Set(windowInvoices.map(i => i._id));
        setWindowInvoices(current => [...rows.filter(r => !known.has(r._id)), ...current]);
        setIndexOffset(prevPage * pageSize);
      }
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle direct invoice search - load all matching invoices
  const handleDirectInvoiceSearch = async (invoiceNumber: string): Promise<boolean> => {
    if (!invoiceNumber.trim()) return false;

    const { _id: accountId } = selectedAccount;
    if (accountId && isLoggedIn) {
      try {
        // Don't set loading for the entire page, just for search

        // Search for all invoices matching the number pattern (no limit)
        const result: any = await listInvoices({
          accountId,
          type: voucherType === 'sale' || voucherType === 'sales' ? VocuherCategory.SALE : VocuherCategory.PURCHASE,
          params: `&invoiceNumber=${encodeURIComponent(invoiceNumber)}` // Remove limit to get all matching
        });

        const foundInvoices = result.payload?.invoices || [];

        if (foundInvoices.length > 0) {
          // Replace current invoice list with search results
          // This will update the navigation to only show matching invoices
          console.log(`Found ${foundInvoices.length} invoices matching "${invoiceNumber}"`);
          // The matches become the whole navigable set until the search is cleared.
          setWindowInvoices(foundInvoices);
          setIndexOffset(0);
          setTotalCount(foundInvoices.length);

          // Load the first matching invoice
          const firstInvoice = foundInvoices[0];

          // Fetch the full invoice details for the first invoice
          await loadInvoiceDetails(firstInvoice._id);

          // Navigate to the first invoice
          handleInvoiceChange(
            firstInvoice._id,
            firstInvoice.pdfTemplate || templateId || '',
            firstInvoice.voucherCategory || voucherType || VocuherCategory.SALE
          );

          // Return true to indicate search was successful
          return true;
        } else {
          // Don't break the UI, just show a message
          alert(`No invoices found matching "${invoiceNumber}". Please try a different search.`);
          // Keep the existing invoice list intact
          return false;
        }
      } catch (error) {
        console.error('Error searching invoice:', error);
        alert('Error searching for invoice. Please try again.');
        return false;
      }
    }
    return false;
  };

  return (
    <>
      <Box sx={{
        backgroundColor: '#fafafb',
        minHeight: '100vh',
        pt: 0,
        pb: 2 }}>
        {/* Top Section with Navigation Bar, Party Details and Quick Stats */}
        {isLoggedIn && (
          <Box sx={{ p: { xs: 1, sm: 2 }, pb: 1 }}>
            <Grid container spacing={2}>
              {/* Navigation Bar and Actions - Left Side */}

              <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                {currentInvoiceId && (
                  <InvoiceNavigationBar
                    currentInvoiceId={currentInvoiceId || ''}
                    voucherCategory={details?.voucherCategory || ''}
                    voucherType={voucherType}
                    onInvoiceChange={handleInvoiceChange}
                    invoices={windowInvoices}
                    currentInvoiceDetails={details}
                    onActionChange={actionChangeHandler}
                    onLoadMore={handleLoadMore}
                    onLoadPrevious={handleLoadPrevious}
                    hasMoreData={hasMoreData}
                    hasPrevData={hasPrevData}
                    loadingMore={loadingMore || listLoading}
                    onDirectSearch={handleDirectInvoiceSearch}
                    totalCount={totalCount}
                    indexOffset={indexOffset}
                    onFiltersChange={setFilters}
                  />
                )}
                </Box>
                {props.onClose && <IconButton onClick={props.onClose} sx={{ color: 'error.main' }}><HighlightOffTwoToneIcon fontSize="large" /></IconButton>}
              </Grid>
            </Grid>
          </Box>
        )}

        <Grid container spacing={2} sx={{
          p: { xs: 1, sm: 2 }
        }}>
          <Grid item xs={12} md={8} sx={{
            order: { xs: 2, md: 1 },
            mt: { xs: 2, md: 0 }
          }}>
            {(isLoading ?
              <Box sx={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                p: 3,
                height: { xs: '60vh', md: '85vh' }
              }}>
                <Grid container>
                  <Grid item xs={12} style={{ textAlign: 'center' }}>
                    <CircularProgress color="info" />
                    <Typography sx={{ mt: 2 }}>Loading Preview ...</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton width="100%" height={300} />
                  </Grid>
                </Grid>
              </Box>
              :
              <Box sx={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                height: { xs: '60vh', md: '85vh' },
                display: 'flex',
                flexDirection: 'column'
              }}>
                {canRegenerate && !pdfMissing && previousPdfLocation && (
                  // Only the compare toggle lives here now; the regenerate escape hatch
                  // stays on the pdfMissing state, where it is actually needed.
                  <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}
                    sx={{ px: 1.5, py: 0.5, borderBottom: '1px solid #e9ecf1', bgcolor: '#fafafb' }}>
                    {previousPdfLocation && (
                      <Button size="small" variant={showComparison ? 'contained' : 'outlined'}
                        startIcon={<CompareArrowsIcon />}
                        onClick={() => setShowComparison(v => !v)}
                        sx={{ textTransform: 'none' }}>
                        {showComparison ? 'Exit compare' : 'Compare with previous'}
                      </Button>
                    )}
                  </Stack>
                )}
                {showComparison && previousPdfLocation ? (
                  <Box sx={{ flex: 1, p: 1 }}>
                    <InvoicePdfComparison
                      beforeUrl={previousPdfLocation}
                      afterUrl={pdfLocation}
                      height="100%"
                    />
                  </Box>
                ) : pdfMissing ? (
                  <PdfMissingState
                    pdfError={pdfError}
                    canRegenerate={canRegenerate}
                    isRegenerating={isRegenerating}
                    isLoggedIn={isLoggedIn}
                    onRegenerate={handleRegeneratePdf}
                    documentLabel={details?.voucherType}
                  />
                ) : (
                  <object
                    key={viewerKey}
                    style={{ flex: 1, width: "100%" }}
                    data={pdfLocation}
                    type="application/pdf"
                  >
                    {/* Browsers render this fallback when the PDF fails to load (404 / blocked). */}
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', p: 3, textAlign: 'center' }}>
                      <PictureAsPdfOutlinedIcon sx={{ fontSize: 56, color: '#b0b7c3', mb: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>Invoice PDF could not be loaded</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The PDF file seems to be missing or broken.
                      </Typography>
                      {canRegenerate && (
                        <LoadingButton variant="contained" color="primary" loading={isRegenerating}
                          startIcon={<AutorenewIcon />} onClick={handleRegeneratePdf}>
                          Generate PDF
                        </LoadingButton>
                      )}
                    </Box>
                  </object>
                )}
              </Box>
            )}
          </Grid>

          {/* Right side - Header and Party Details */}
          <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>

            {/* Enhanced Party Details Section */}
            <Box sx={{
              borderRadius: '12px',
              border: '1px solid #e9ecf1',
              backgroundColor: '#fff',
              display: { xs: 'none', sm: 'block' }
            }}>
              <Box sx={{
                bgcolor: '#f5f5f5',
                p: { xs: 1.5, sm: 2 },
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                      }}
                    >
                      {details?.voucherType || 'Invoice'} Preview
                    </Typography>
                <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                    >
                      {details?.date
                        ? new Date(details.date).toLocaleDateString('en-IN')
                        : 'N/A'} • #{details?.invoiceNumber || 'N/A'}
                    </Typography>
              </Box>
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box sx={DETAIL_GRID_SX}>

                  <Box className={FULL_ROW_CLASS}>
                    <Typography variant="body2" color="text.secondary">
                      Party Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {details?.buyer?.name || details?.supplier?.name || 'N/A'}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box className={FULL_ROW_CLASS}>
                    <Typography variant="body2" color="text.secondary">
                      Invoice Amount
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                      }}
                    >
                      {/* { Utils.formatNumber(parseFloat(details?.amount + Utils.additionalChargesSum(details)).toFixed(2) || '0') } */}

                      {previewAmount}
                    </Typography>
                  </Box>

                  {/* Same order as the receipt preview: Date | Type | Status, then Received | Balance. */}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Invoice Date
                    </Typography>
                    <Typography variant="body1">
                      {details?.date
                        ? new Date(details.date).toLocaleDateString('en-IN')
                        : 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Invoice Type
                    </Typography>
                    <Typography variant="body1">
                      {details?.voucherType || 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Invoice Status
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: details?.status === 'PAID'
                          ? 'success.main'
                          : 'warning.main'
                      }}
                    >
                      {details?.status ? String(details.status).replace(/_/g, ' ') : 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Received Amount
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'success.main' }}>
                      {Utils.formatNumber((Number(details?.receivedAmount) || 0).toFixed(2))}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Balance Amount
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: (Number(details?.balanceAmount) || 0) > 0 ? 'warning.main' : 'text.primary' }}>
                      {Utils.formatNumber((Number(details?.balanceAmount) || 0).toFixed(2))}
                    </Typography>
                  </Box>

                  {details?.refInvoiceNumber && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Reference Invoice
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {details.refInvoiceNumber}
                      </Typography>
                    </Box>
                  )}

                  {invoiceTags.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Tags
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                        {invoiceTags.map((tag: any, i: number) => (
                          <Chip
                            key={tag._id || i}
                            label={tag.name}
                            size="small"
                            style={{ borderRadius: 7, color: '#488dff', backgroundColor: '#f1f0fc' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Received / Balance are listed above, so only the payment rows here. */}
                  <InvoicePaymentBreakdown payments={payments} loading={paymentsLoading} showTotals={false} />

                </Box>
              </Box>
            </Box>

          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default InvoicePreviewWithFilters;
